/**
 * Admin Stripe Connect — host payouts overview.
 *
 * Ops-facing table: which hosts have connected, which are pending, and how
 * much ledger balance still sits unpaid. No write actions (payouts are
 * managed directly from Stripe's dashboard). Designed to be opened from
 * AdminPanel header → "Stripe Connect" link.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Wallet, CheckCircle2, Clock3, AlertTriangle } from "lucide-react";
import { useTruthState } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatGbp(pence) {
  const v = (pence || 0) / 100;
  if (Number.isInteger(v)) return `£${v}`;
  return `£${v.toFixed(2)}`;
}

export default function AdminStripeConnect() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Batch P1 · Operations Truth-State — read-only view; only failure
  // path emits a canonical `failed` receipt with Retry.
  const truth = useTruthState();
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/stripe-connect/admin/hosts`, { withCredentials: true })
      .then((r) => { setRows(r.data?.hosts || []); truth.setState(null); })
      .catch((err) => {
        truth.setState(
          "failed",
          err?.response?.data?.detail || "Couldn't load host list.",
        );
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const totalOutstanding = rows
    .filter((r) => !r.onboarded)
    .reduce((s, r) => s + Math.max(0, r.ledger_balance_pence), 0);
  const onboardedCount = rows.filter((r) => r.onboarded).length;
  const pendingCount = rows.filter((r) => r.connect.account_id && !r.onboarded).length;

  return (
    <div data-testid="admin-stripe-connect-page" className="min-h-dvh no-overflow-x bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/admin")}
            className="p-2 rounded-xl hover:bg-slate-100"
            data-testid="admin-connect-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900">Stripe Connect hosts</h1>
            <p className="text-[11px] text-slate-500">
              Host payout state + outstanding ledger balance for manual payouts
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5 space-y-4">
        <TruthReceipt
          state={truth.state}
          message={truth.message}
          onRetry={load}
          onDismiss={() => truth.setState(null)}
        />
        {/* Summary tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div
            data-testid="connect-summary-onboarded"
            className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4"
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-700" />
              <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                Onboarded
              </p>
            </div>
            <p className="text-3xl font-black text-emerald-900 mt-1">{onboardedCount}</p>
          </div>
          <div
            data-testid="connect-summary-pending"
            className="bg-amber-50 border border-amber-200 rounded-2xl p-4"
          >
            <div className="flex items-center gap-2">
              <Clock3 className="w-4 h-4 text-amber-700" />
              <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
                Pending
              </p>
            </div>
            <p className="text-3xl font-black text-amber-900 mt-1">{pendingCount}</p>
          </div>
          <div
            data-testid="connect-summary-outstanding"
            className="bg-rose-50 border border-rose-200 rounded-2xl p-4"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-700" />
              <p className="text-[11px] font-bold uppercase tracking-wider text-rose-700">
                Manual payout owed
              </p>
            </div>
            <p className="text-3xl font-black text-rose-900 mt-1">
              {formatGbp(totalOutstanding)}
            </p>
            <p className="text-[10px] text-rose-700 mt-0.5">
              Sum of ledger balance for non-onboarded hosts
            </p>
          </div>
        </div>

        {/* Host table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-indigo-600" />
            <p className="text-sm font-bold text-slate-900">All hosts</p>
            <span className="text-[11px] text-slate-400 ml-auto">{rows.length} hosts</span>
          </div>
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">
              No host activity yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="text-left px-4 py-2">Host</th>
                    <th className="text-left px-4 py-2">Role</th>
                    <th className="text-left px-4 py-2">Connect</th>
                    <th className="text-right px-4 py-2">Ledger</th>
                    <th className="text-right px-4 py-2">Account ID</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.user_id}
                      data-testid={`admin-connect-row-${r.user_id}`}
                      className="border-t border-slate-200 hover:bg-slate-50"
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-900">{r.name}</p>
                        <p className="text-[11px] text-slate-500">{r.email}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs uppercase">
                        {r.role || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {r.onboarded ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">
                            <CheckCircle2 className="w-3 h-3" />
                            Onboarded
                          </span>
                        ) : r.connect.account_id ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">
                            <Clock3 className="w-3 h-3" />
                            Pending
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">
                            Ledger only
                          </span>
                        )}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-bold tabular-nums ${
                          r.ledger_balance_pence > 0
                            ? r.onboarded
                              ? "text-emerald-700"
                              : "text-rose-700"
                            : "text-slate-400"
                        }`}
                      >
                        {formatGbp(r.ledger_balance_pence)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.connect.account_id ? (
                          <code className="text-[10px] text-slate-500">
                            {r.connect.account_id}
                          </code>
                        ) : (
                          <span className="text-[10px] text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[11px] text-slate-500 text-center">
          Payouts to onboarded hosts happen automatically on Stripe's default schedule.
          Non-onboarded hosts' balances must be paid out manually via Stripe dashboard.
        </p>
      </div>
    </div>
  );
}
